import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Platform,
} from 'react-native';

export function relatedSelectionKey(item) {
  return `${item.object_api_name}:${item.id}`;
}

function RelatedCheckbox({ checked, onPress, theme }) {
  const gold = theme.colors.primary || '#d4af37';
  return (
    <TouchableOpacity
      onPress={onPress}
      style={stylesCheckbox.hit}
      accessibilityRole="checkbox"
      accessibilityState={{ checked }}
      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
    >
      <View
        style={[
          stylesCheckbox.box,
          checked && stylesCheckbox.boxChecked,
          {
            borderColor: gold,
            backgroundColor: checked
              ? gold
              : theme.isDarkMode
                ? '#1a1a1a'
                : '#FFFFFF',
          },
        ]}
      >
        {checked ? <Text style={stylesCheckbox.checkmark}>✓</Text> : null}
      </View>
    </TouchableOpacity>
  );
}

const stylesCheckbox = StyleSheet.create({
  hit: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 4,
    backgroundColor: 'transparent',
  },
  box: {
    width: 22,
    height: 22,
    borderRadius: 4,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  boxChecked: {
    borderColor: 'transparent',
  },
  checkmark: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: 'bold',
    lineHeight: 18,
    backgroundColor: 'transparent',
  },
});

function RelatedRecordRow({
  item,
  selected,
  onToggleSelect,
  onOpenRecord,
  enableBulkSelect,
  theme,
  styles,
}) {
  return (
    <View style={styles.relatedRow}>
      {enableBulkSelect ? (
        <RelatedCheckbox
          checked={selected}
          onPress={() => onToggleSelect(item)}
          theme={theme}
        />
      ) : null}
      <TouchableOpacity
        style={styles.relatedRowPressable}
        onPress={() => onOpenRecord(item)}
        activeOpacity={0.7}
        {...(Platform.OS === 'web' ? { dataSet: { recordRelatedRow: 'true' } } : {})}
      >
        <View style={styles.relatedRowMain}>
          <Text style={[styles.relatedRowTitle, { color: theme.colors.text }]} numberOfLines={1}>
            {item.title}
          </Text>
          <Text style={[styles.relatedRowMeta, { color: theme.colors.text }]}>
            {item.object_label} · ID {item.id}
          </Text>
        </View>
        <Text style={[styles.relatedRowChevron, { color: theme.colors.primary || '#d4af37' }]}>
          ›
        </Text>
      </TouchableOpacity>
    </View>
  );
}

function LookupCard({ lookup, onPress, theme, styles }) {
  return (
    <TouchableOpacity
      style={[styles.lookupCard, { borderColor: theme.colors.border }]}
      onPress={() =>
        onPress({
          object_api_name: lookup.related_object_api_name,
          object_label: lookup.related_object_label,
          id: lookup.related_record_id,
          title: lookup.related_record_title,
        })
      }
      activeOpacity={0.7}
      {...(Platform.OS === 'web' ? { dataSet: { recordRelatedLookup: 'true' } } : {})}
    >
      <Text style={[styles.lookupLabel, { color: theme.colors.text }]}>{lookup.label}</Text>
      <Text
        style={[styles.lookupValue, { color: theme.colors.primary || '#d4af37' }]}
        numberOfLines={1}
      >
        {lookup.related_record_title}
      </Text>
      <Text style={[styles.lookupMeta, { color: theme.colors.text }]}>
        {lookup.related_object_label} · ID {lookup.related_record_id}
      </Text>
    </TouchableOpacity>
  );
}

export default function RecordRelatedLists({
  relatedData,
  loading,
  error,
  onOpenRecord,
  theme,
  enableBulkSelect = false,
  selectedKeys = [],
  onToggleSelect,
}) {
  const styles = getStyles(theme);
  const selectedSet = new Set(selectedKeys);

  if (loading) {
    return (
      <View style={styles.centerState}>
        <ActivityIndicator size="large" color="#d4af37" />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.centerState}>
        <Text style={styles.errorText}>{error}</Text>
      </View>
    );
  }

  const parentLookups = relatedData?.parent_lookups || [];
  const childRelationships = relatedData?.child_relationships || [];
  const hasContent = parentLookups.length > 0 || childRelationships.length > 0;

  if (!hasContent) {
    return (
      <View style={styles.emptyCard}>
        <Text style={[styles.emptyText, { color: theme.colors.text }]}>
          No related records found for this record.
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.wrap}>
      {parentLookups.length > 0 ? (
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: theme.colors.heading }]}>Lookup relationships</Text>
          <Text style={[styles.sectionSub, { color: theme.colors.text }]}>
            Parent records referenced by this record.
          </Text>
          {parentLookups.map((lookup) => (
            <LookupCard
              key={lookup.relationship_key}
              lookup={lookup}
              onPress={onOpenRecord}
              theme={theme}
              styles={styles}
            />
          ))}
        </View>
      ) : null}

      {childRelationships.map((rel) => (
        <View key={rel.relationship_key} style={styles.section}>
          <View style={styles.sectionHeadRow}>
            <Text style={[styles.sectionTitle, { color: theme.colors.heading }]}>
              {rel.label}
            </Text>
            <Text style={[styles.sectionCount, { color: theme.colors.text }]}>
              {rel.total} record{rel.total === 1 ? '' : 's'}
            </Text>
          </View>
          {rel.via_junction_table ? (
            <Text style={[styles.sectionSub, { color: theme.colors.text }]}>
              Related via {rel.via_junction_table.replace(/_/g, ' ')}
            </Text>
          ) : null}
          {rel.records.length === 0 ? (
            <Text style={[styles.emptyHint, { color: theme.colors.text }]}>No records.</Text>
          ) : (
            rel.records.map((item) => (
              <RelatedRecordRow
                key={`${rel.relationship_key}-${item.id}`}
                item={item}
                selected={selectedSet.has(relatedSelectionKey(item))}
                onToggleSelect={onToggleSelect}
                onOpenRecord={onOpenRecord}
                enableBulkSelect={enableBulkSelect}
                theme={theme}
                styles={styles}
              />
            ))
          )}
          {rel.total > rel.records.length ? (
            <Text style={[styles.moreHint, { color: theme.colors.text }]}>
              Showing {rel.records.length} of {rel.total}
            </Text>
          ) : null}
        </View>
      ))}
    </View>
  );
}

function getStyles(theme) {
  const cardBg = theme.isDarkMode ? theme.colors.surface : '#ffffff';

  return StyleSheet.create({
    wrap: {
      gap: 16,
      backgroundColor: 'transparent',
    },
    centerState: {
      paddingVertical: 48,
      alignItems: 'center',
      backgroundColor: 'transparent',
    },
    errorText: {
      color: '#ba0517',
      fontWeight: '600',
      backgroundColor: 'transparent',
    },
    emptyCard: {
      backgroundColor: cardBg,
      borderRadius: 4,
      borderWidth: 1,
      borderColor: theme.colors.border,
      padding: 20,
    },
    emptyText: {
      fontSize: 14,
      fontStyle: 'italic',
      backgroundColor: 'transparent',
    },
    section: {
      backgroundColor: cardBg,
      borderRadius: 4,
      borderWidth: 1,
      borderColor: theme.colors.border,
      padding: 14,
    },
    sectionHeadRow: {
      flexDirection: 'row',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      gap: 8,
      backgroundColor: 'transparent',
    },
    sectionTitle: {
      fontSize: 15,
      fontWeight: '700',
      backgroundColor: 'transparent',
    },
    sectionCount: {
      fontSize: 12,
      opacity: 0.85,
      backgroundColor: 'transparent',
    },
    sectionSub: {
      fontSize: 12,
      opacity: 0.85,
      marginTop: 4,
      marginBottom: 10,
      backgroundColor: 'transparent',
    },
    lookupCard: {
      borderWidth: 1,
      borderRadius: 4,
      padding: 12,
      marginBottom: 8,
      backgroundColor: 'transparent',
    },
    lookupLabel: {
      fontSize: 11,
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: 0.4,
      opacity: 0.85,
      backgroundColor: 'transparent',
    },
    lookupValue: {
      fontSize: 16,
      fontWeight: '600',
      marginTop: 4,
      backgroundColor: 'transparent',
    },
    lookupMeta: {
      fontSize: 11,
      marginTop: 4,
      opacity: 0.8,
      backgroundColor: 'transparent',
    },
    relatedRow: {
      flexDirection: 'row',
      alignItems: 'center',
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.colors.border,
      backgroundColor: 'transparent',
    },
    relatedRowPressable: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 10,
      backgroundColor: 'transparent',
    },
    relatedRowMain: {
      flex: 1,
      minWidth: 0,
      backgroundColor: 'transparent',
    },
    relatedRowTitle: {
      fontSize: 14,
      fontWeight: '600',
      backgroundColor: 'transparent',
    },
    relatedRowMeta: {
      fontSize: 11,
      marginTop: 2,
      opacity: 0.8,
      backgroundColor: 'transparent',
    },
    relatedRowChevron: {
      fontSize: 22,
      fontWeight: '300',
      marginLeft: 8,
      backgroundColor: 'transparent',
    },
    emptyHint: {
      fontSize: 13,
      fontStyle: 'italic',
      backgroundColor: 'transparent',
    },
    moreHint: {
      fontSize: 11,
      marginTop: 8,
      opacity: 0.75,
      backgroundColor: 'transparent',
    },
  });
}
